import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';

interface AlertConfig {
  enabled: boolean;
  discordWebhookUrl: string;
  pendingMinutes: number;
  sendingMinutes: number;
}

const ALERT_CONFIG_KEY = 'dispatch_message_alert';
const SYSTEM_CONFIG_RESOURCE = 'admin/system-config';

// Mesmos limites e defaults do Joi do backend, que é o gate de verdade.
const PENDING_RANGE = { min: 5, max: 240 };
const SENDING_RANGE = { min: 30, max: 720 };

const EMPTY_ALERT_CONFIG: AlertConfig = {
  enabled: true,
  discordWebhookUrl: '',
  pendingMinutes: 20,
  sendingMinutes: 180,
};

const SLATE = '#64748b';

const inRange = (value: number, { min, max }: { min: number; max: number }) =>
  Number.isInteger(value) && value >= min && value <= max;

const DispatchMessageAlertConfigDialog: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [config, setConfig] = useState<AlertConfig>(EMPTY_ALERT_CONFIG);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    dataProvider.getOne(SYSTEM_CONFIG_RESOURCE, { id: ALERT_CONFIG_KEY })
      .then((res) => {
        const { id, ...rest } = (res.data ?? {}) as any;
        setConfig({ ...EMPTY_ALERT_CONFIG, ...rest, discordWebhookUrl: rest.discordWebhookUrl ?? '' });
      })
      .catch(() => notify('Não foi possível carregar a configuração do alerta', { type: 'error' }))
      .finally(() => setLoading(false));
  }, [open, dataProvider, notify]);

  const patch = (fields: Partial<AlertConfig>) => setConfig((c) => ({ ...c, ...fields }));

  const webhook = config.discordWebhookUrl.trim();
  const webhookInvalid = !!webhook && !/^https:\/\/\S+$/.test(webhook);
  const pendingInvalid = !inRange(Number(config.pendingMinutes), PENDING_RANGE);
  const sendingInvalid = !inRange(Number(config.sendingMinutes), SENDING_RANGE);

  const handleSave = async () => {
    if (webhookInvalid || pendingInvalid || sendingInvalid) {
      notify('Corrija os campos destacados antes de salvar.', { type: 'warning' });
      return;
    }
    if (config.enabled && !webhook) {
      notify('Informe a URL do webhook do Discord para ativar o alerta.', { type: 'warning' });
      return;
    }

    setSaving(true);
    try {
      // previousData vazio de propósito: o dataProvider faz dirty-diff por chave e
      // enviaria só os campos alterados — o backend valida o payload inteiro.
      await dataProvider.update(SYSTEM_CONFIG_RESOURCE, {
        id: ALERT_CONFIG_KEY,
        data: {
          enabled: config.enabled,
          discordWebhookUrl: webhook,
          pendingMinutes: Number(config.pendingMinutes),
          sendingMinutes: Number(config.sendingMinutes),
        },
        previousData: {} as any,
      });
      notify('Configuração do alerta salva', { type: 'success' });
      onClose();
    } catch (e: any) {
      notify(e?.message || 'Erro ao salvar a configuração', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Alerta de envio</DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <FormControlLabel
          control={<Switch checked={config.enabled} onChange={(e) => patch({ enabled: e.target.checked })} />}
          label="Avisar no Discord"
        />
        <Typography variant="caption" sx={{ color: SLATE, display: 'block', mb: 2 }}>
          O sistema confere os envios das últimas 48 horas e avisa quando um deles não saiu, travou no meio ou falhou.
          Desligado, nada é enviado ao Discord.
        </Typography>

        <Box sx={{ width: 460, maxWidth: '100%', mb: 3 }}>
          <TextField
            size="small"
            label="Webhook do Discord"
            value={config.discordWebhookUrl}
            onChange={(e) => patch({ discordWebhookUrl: e.target.value })}
            placeholder="https://discord.com/api/webhooks/..."
            error={webhookInvalid}
            helperText={webhookInvalid
              ? 'A URL precisa começar com https://'
              : 'Canal que recebe o aviso. A URL já é o destino e o segredo.'}
            sx={{ width: '100%' }}
          />
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Envio que não saiu</Typography>
        <Typography variant="caption" sx={{ color: SLATE, display: 'block', mb: 1.5 }}>
          Avisa se um envio continua &quot;Agendado&quot; este tempo depois do horário marcado.
        </Typography>
        <TextField
          size="small"
          type="number"
          label="Minutos"
          value={config.pendingMinutes}
          onChange={(e) => patch({ pendingMinutes: Number(e.target.value) })}
          error={pendingInvalid}
          helperText={`De ${PENDING_RANGE.min} a ${PENDING_RANGE.max}`}
          inputProps={PENDING_RANGE}
          sx={{ width: 160, mb: 3, display: 'block' }}
        />

        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Envio travado</Typography>
        <Typography variant="caption" sx={{ color: SLATE, display: 'block', mb: 1.5 }}>
          Avisa se um envio continua &quot;Enviando&quot; este tempo depois do horário marcado. Envios grandes demoram,
          por isso o prazo é maior.
        </Typography>
        <TextField
          size="small"
          type="number"
          label="Minutos"
          value={config.sendingMinutes}
          onChange={(e) => patch({ sendingMinutes: Number(e.target.value) })}
          error={sendingInvalid}
          helperText={`De ${SENDING_RANGE.min} a ${SENDING_RANGE.max}`}
          inputProps={SENDING_RANGE}
          sx={{ width: 160, mb: 2, display: 'block' }}
        />
        <Typography variant="caption" sx={{ color: SLATE, display: 'block' }}>
          Envios com status &quot;Falha&quot; sempre geram aviso.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading} sx={{ textTransform: 'none' }}>
          {saving ? 'Salvando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DispatchMessageAlertConfigDialog;
